import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  QrCode, Search, CheckCircle, XCircle, 
  Camera, CameraOff, Volume2, Smartphone, RotateCcw, Flashlight, Maximize2
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Html5QrcodeScanner, Html5Qrcode } from "html5-qrcode";

export default function TicketVerifyPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState("");
  const [cameraId, setCameraId] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    // Get available cameras on mount
    Html5Qrcode.getCameras().then(cameras => {
      if (cameras && cameras.length) {
        setAvailableCameras(cameras);
        // Prefer back camera
        const backCamera = cameras.find(c => 
          c.label.toLowerCase().includes('back') || 
          c.label.toLowerCase().includes('rear') ||
          c.label.toLowerCase().includes('environment')
        );
        setCameraId(backCamera?.id || cameras[cameras.length - 1].id);
      }
    }).catch(err => console.error("Error getting cameras:", err));

    return () => {
      if (html5QrCodeRef.current) {
        try {
          const state = html5QrCodeRef.current.getState();
          if (state === 2) { // SCANNING state
            html5QrCodeRef.current.stop().catch(() => {});
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?upcoming=true`, { withCredentials: true });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0].event_id);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const params = { q: searchQuery };
      if (selectedEvent && selectedEvent !== "all") {
        params.event_id = selectedEvent;
      }
      const response = await axios.get(`${API}/tickets/search`, {
        params,
        withCredentials: true
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching tickets:", error);
      toast.error("Search failed");
    }
  };

  const verifyAndCheckIn = async (ticketData) => {
    if (ticketData === lastScannedCode) return;
    setLastScannedCode(ticketData);
    setProcessing(true);
    
    try {
      const verifyResponse = await axios.post(`${API}/tickets/verify`, {
        qr_data: ticketData
      }, { withCredentials: true });
      
      setScanResult(verifyResponse.data);
      
      if (verifyResponse.data.valid && verifyResponse.data.ticket?.status === "valid") {
        try {
          const checkInResponse = await axios.post(`${API}/tickets/checkin`, {
            qr_data: ticketData
          }, { withCredentials: true });
          
          toast.success(`✓ ${checkInResponse.data.ticket.attendee_name} checked in!`, {
            duration: 3000,
          });
          
          setScanResult({
            valid: true,
            message: "Checked in successfully!",
            ticket: checkInResponse.data.ticket
          });
          
          // Vibrate on success (mobile)
          if (navigator.vibrate) {
            navigator.vibrate(200);
          }
          // Play success sound
          playBeep(true);
        } catch (checkInError) {
          toast.error("Check-in failed");
        }
      } else if (verifyResponse.data.ticket?.status === "used") {
        toast.warning(`Already checked in: ${verifyResponse.data.ticket.attendee_name}`, {
          duration: 3000,
        });
        if (navigator.vibrate) navigator.vibrate([100, 100, 100]);
        playBeep(false);
      } else {
        toast.error(verifyResponse.data.message || "Invalid ticket");
        if (navigator.vibrate) navigator.vibrate(500);
        playBeep(false);
      }
    } catch (error) {
      console.error("Error verifying ticket:", error);
      setScanResult({ valid: false, message: "Verification failed" });
      toast.error("Verification failed");
    } finally {
      setProcessing(false);
      setTimeout(() => setLastScannedCode(""), 2000);
    }
  };

  const playBeep = (success) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = success ? 800 : 300;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + (success ? 0.15 : 0.3));
    } catch (e) {
      console.log("Audio not supported");
    }
  };

  const startScanning = async () => {
    setScanning(true);
    setScanResult(null);
  };

  // Initialize scanner when scanning state changes to true
  useEffect(() => {
    if (!scanning) return;
    
    const initScanner = async () => {
      // Wait for DOM element to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const qrReaderElement = document.getElementById('qr-reader');
      if (!qrReaderElement) {
        console.error("QR reader element not found");
        toast.error("Scanner initialization failed. Please try again.");
        setScanning(false);
        return;
      }
      
      try {
        html5QrCodeRef.current = new Html5Qrcode("qr-reader");
        
        const qrCodeSuccessCallback = (decodedText) => {
          verifyAndCheckIn(decodedText);
        };
        
        // Optimized config for mobile
        const config = { 
          fps: 15, 
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            const qrboxSize = Math.floor(minEdge * 0.7);
            return { width: qrboxSize, height: qrboxSize };
          },
          aspectRatio: 1.0,
          disableFlip: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        };
        
        // Use selected camera or fallback to environment facing
        if (cameraId) {
          await html5QrCodeRef.current.start(
            cameraId,
            config,
            qrCodeSuccessCallback,
            () => {}
          );
        } else {
          await html5QrCodeRef.current.start(
            { facingMode: "environment" },
            config,
            qrCodeSuccessCallback,
            () => {}
          );
        }
      } catch (error) {
        console.error("Error starting scanner:", error);
        const errorMessage = error.name === 'NotFoundError' 
          ? "No camera found. Please use the manual entry or try on a device with a camera."
          : error.name === 'NotAllowedError'
          ? "Camera permission denied. Please allow camera access and try again."
          : "Could not access camera. Please check permissions or use manual entry.";
        toast.error(errorMessage);
        setScanning(false);
      }
    };
    
    initScanner();
  }, [scanning, cameraId]);

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current = null;
      } catch (error) {
        console.error("Error stopping scanner:", error);
      }
    }
    setScanning(false);
  };

  const switchCamera = async () => {
    if (availableCameras.length < 2) return;
    
    await stopScanning();
    const currentIndex = availableCameras.findIndex(c => c.id === cameraId);
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    setCameraId(availableCameras[nextIndex].id);
    
    // Restart with new camera after state updates
    setTimeout(() => setScanning(true), 300);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleManualInput = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const qrCode = formData.get('qrCode');
    if (qrCode) {
      verifyAndCheckIn(qrCode);
      e.target.reset();
    }
  };

  const manualCheckIn = async (ticket) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/tickets/checkin`, {
        qr_data: ticket.qr_data || ticket.ticket_id
      }, { withCredentials: true });
      
      toast.success(`✓ ${response.data.ticket.attendee_name} checked in!`);
      
      setSearchResults(prev => prev.map(t => 
        t.ticket_id === response.data.ticket.ticket_id 
          ? { ...t, status: "used", checked_in_at: new Date().toISOString() }
          : t
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Layout>
      {/* Compact Header for Mobile */}
      <section className="bg-deep-blue py-4 md:py-8" data-testid="verify-hero">
        <div className="container-custom">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-full flex items-center justify-center">
              <QrCode className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white font-heading">Ticket Scanner</h1>
              <p className="text-white/80 text-sm hidden md:block">Scan QR codes or search attendees</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-4 md:py-8" data-testid="verify-section">
        <div className="container-custom">
          {/* Event Selection - Always visible */}
          <div className="mb-4">
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="w-full md:w-80" data-testid="event-select">
                <SelectValue placeholder="Select event to scan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {events.map(event => (
                  <SelectItem key={event.event_id} value={event.event_id}>
                    {event.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* QR Scanner - Primary on Mobile */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Scanner Area */}
                <div className="relative bg-black">
                  {scanning ? (
                    <div className="relative">
                      <div id="qr-reader" className="w-full" style={{ minHeight: '300px' }}></div>
                      <Button 
                        onClick={stopScanning}
                        variant="destructive"
                        size="lg"
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 shadow-lg"
                        data-testid="stop-scan-btn"
                      >
                        <CameraOff className="w-5 h-5 mr-2" />
                        Stop Scanner
                      </Button>
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <div className="w-24 h-24 mx-auto mb-4 border-4 border-dashed border-slate-600 rounded-lg flex items-center justify-center">
                        <Camera className="w-12 h-12 text-slate-500" />
                      </div>
                      <Button 
                        onClick={startScanning}
                        size="lg"
                        className="btn-primary text-lg px-8"
                        data-testid="start-scan-btn"
                      >
                        <Camera className="w-5 h-5 mr-2" />
                        Start Camera Scanner
                      </Button>
                      <p className="text-slate-400 text-sm mt-4">
                        <Smartphone className="w-4 h-4 inline mr-1" />
                        Works best on phone/tablet cameras
                      </p>
                    </div>
                  )}
                </div>

                {/* Manual Input */}
                <div className="p-4 border-t border-slate-200">
                  <form onSubmit={handleManualInput} className="flex gap-2">
                    <Input 
                      name="qrCode"
                      placeholder="Enter ticket ID manually"
                      className="flex-1"
                      data-testid="manual-qr-input"
                    />
                    <Button type="submit" disabled={processing} data-testid="verify-btn">
                      Verify
                    </Button>
                  </form>
                </div>
              </div>

              {/* Scan Result - Large & Clear for Event Staff */}
              {scanResult && (
                <div 
                  className={`p-6 rounded-lg ${
                    scanResult.valid && scanResult.ticket?.status !== "used"
                      ? 'bg-green-100 border-2 border-green-500' 
                      : scanResult.ticket?.status === "used"
                      ? 'bg-yellow-100 border-2 border-yellow-500'
                      : 'bg-red-100 border-2 border-red-500'
                  }`}
                  data-testid="scan-result"
                >
                  <div className="flex items-start gap-4">
                    {scanResult.valid && scanResult.ticket?.status !== "used" ? (
                      <CheckCircle className="w-12 h-12 text-green-600 flex-shrink-0" />
                    ) : scanResult.ticket?.status === "used" ? (
                      <XCircle className="w-12 h-12 text-yellow-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-12 h-12 text-red-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-bold text-xl ${
                        scanResult.valid && scanResult.ticket?.status !== "used"
                          ? 'text-green-800' 
                          : scanResult.ticket?.status === "used"
                          ? 'text-yellow-800'
                          : 'text-red-800'
                      }`}>
                        {scanResult.message}
                      </h4>
                      
                      {scanResult.ticket && (
                        <div className="mt-3 space-y-2">
                          <p className="text-2xl font-bold text-slate-800">{scanResult.ticket.attendee_name}</p>
                          <p className="text-slate-600">{scanResult.ticket.attendee_email}</p>
                          {scanResult.ticket.checked_in_at && (
                            <p className="text-sm text-slate-500">
                              Checked in: {format(new Date(scanResult.ticket.checked_in_at), "h:mm a")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Search - Secondary on Mobile */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6">
                <h3 className="font-semibold text-deep-blue mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Search Attendees
                </h3>
                
                <div className="flex gap-2 mb-4">
                  <Input 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Name, email, or ticket ID"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    data-testid="attendee-search"
                  />
                  <Button onClick={handleSearch} data-testid="search-btn">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>

                {/* Search Results */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((ticket) => (
                      <div 
                        key={ticket.ticket_id}
                        className={`p-4 rounded-lg border-2 ${
                          ticket.status === "used" 
                            ? 'bg-slate-50 border-slate-200' 
                            : 'bg-white border-green-200'
                        }`}
                        data-testid={`search-result-${ticket.ticket_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-bold text-lg text-deep-blue">{ticket.attendee_name}</p>
                            <p className="text-sm text-slate-600">{ticket.attendee_email}</p>
                          </div>
                          <div className="text-right">
                            {ticket.status === "valid" ? (
                              <Button 
                                size="lg"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold"
                                onClick={() => manualCheckIn(ticket)}
                                disabled={processing}
                              >
                                <CheckCircle className="w-5 h-5 mr-2" />
                                CHECK IN
                              </Button>
                            ) : (
                              <div className="text-center">
                                <span className="inline-block px-3 py-2 rounded-full text-sm font-semibold bg-slate-200 text-slate-600">
                                  Already Checked In
                                </span>
                                {ticket.checked_in_at && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    {format(new Date(ticket.checked_in_at), "h:mm a")}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : searchQuery ? (
                    <p className="text-center text-slate-500 py-8">No attendees found</p>
                  ) : (
                    <p className="text-center text-slate-500 py-8">
                      Search by name, email, or ticket ID
                    </p>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              {events.length > 0 && selectedEvent && selectedEvent !== "all" && (
                <div className="bg-slate-100 rounded-lg p-4">
                  <p className="text-sm text-slate-600 text-center">
                    Scanning for: <span className="font-semibold text-deep-blue">
                      {events.find(e => e.event_id === selectedEvent)?.title || "All Events"}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
